import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2025 semantics GmbH
//  Date      : $Date: 2026-05-27 14:18:05 +0200 (Wed, 27 May 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 163319 $
//  Purpose   : wiki components
// =============================================================================
// ---Third-party imports
import React from 'react';
// ---Framework imports
import log from 'uix/logging';
import { Sel } from 'uix/registries';
import { dict, size, isNil } from 'ldash';
import { useSelector, useEffect, useMemo, useRouter, usePersistentWithQueryOverride } from 'uix/hooks';
import { formatDateTime } from 'uix/utils/date';
import { TranslatedTable } from 'uix/components';
// ---Local imports
import styles from '../wiki.css';
import clsx from 'clsx';
export const RevisionTable = ({ accessor, revisions, onSelectionChange, canSelect, noDiffMode = false })=>{
    /* revisions should have datatype:
  [
    {date: string, username: string, someid: custom/accessor}
  ]
  */ let { request } = useRouter();
    let get_id = (rev)=>accessor ? rev[accessor] : rev.id;
    let addDate = (rev)=>({
            ...rev,
            date: new Date(rev.date),
            id: get_id(rev)
        });
    revisions = useMemo(()=>{
        let res = revisions.map(addDate);
        if (accessor == 'owhid' && noDiffMode) {
            res = res.filter((r)=>r.state !== 5); // SAVEPOINT_HIDDEN
        }
        return res;
    }, [
        revisions,
        accessor,
        noDiffMode
    ]);
    let [selectedRevs, setSelectedRevs] = usePersistentWithQueryOverride({
        name: 'revs',
        initialValue: '',
        replace: true
    });
    let selected = useMemo(()=>{
        let rev = String(selectedRevs);
        if (!rev.includes('-')) {
            return {
                left: null,
                right: rev
            };
        }
        return {
            left: rev.split('-')[0],
            right: rev.split('-')[1]
        };
    }, [
        selectedRevs
    ]);
    let setSelected = (s)=>{
        setSelectedRevs(!isNil(s.left) ? `${s.left}-${s.right}` : s.right);
        if (onSelectionChange) {
            onSelectionChange(s);
        }
    };
    let is_selectable = canSelect || ((r)=>true);
    // initialize selected revisions (inform parent component, fallback to data derived defaults)
    useEffect(()=>{
        let revs_with_text = revisions.filter((r)=>is_selectable(r));
        if (size(revs_with_text) > 0) {
            let byId = dict(revs_with_text.map((r)=>[
                    get_id(r),
                    r
                ]));
            if (noDiffMode) {
                // modify on right
                if (byId[selected.right]) {
                    setSelected({
                        left: null,
                        right: selected.right
                    });
                } else {
                    setSelected({
                        left: null,
                        right: get_id(revs_with_text[0])
                    });
                }
            } else {
                if (byId[selected.left] && byId[selected.right]) {
                    // persisted ids are in input revisions list, inform parent component via onSelectionChange
                    setSelected({
                        left: selected.left,
                        right: selected.right
                    });
                } else {
                    let left = size(revs_with_text) > 1 ? get_id(revs_with_text[1]) : null;
                    // choose first ids
                    setSelected({
                        left,
                        right: get_id(revs_with_text[0])
                    });
                }
            }
        }
    }, [
        revisions,
        noDiffMode
    ]);
    let select = (row, side)=>setSelected({
            ...selected,
            [side]: row.id
        });
    let choose = (row)=>{
        if (!is_selectable(row)) {
            return null;
        }
        let leftCss = row.id == selected.left ? styles.histChoiceSelected : styles.histChoice;
        let rightCss = row.id == selected.right ? styles.histChoiceSelected : styles.histChoice;
        let isFirst = revisions[0].id == row.id;
        let isLast = revisions.slice(-1)[0].id == row.id;
        let leftDisabled = isFirst || selected.right == row.id;
        let rightDisabled = isLast || selected.left == row.id;
        leftCss = leftDisabled ? styles.histChoiceDisabled : leftCss;
        rightCss = rightDisabled ? styles.histChoiceDisabled : rightCss;
        return /*#__PURE__*/ _jsxs("span", {
            title: row[accessor],
            children: [
                /*#__PURE__*/ _jsx("span", {
                    className: leftCss,
                    onClick: ()=>!leftDisabled ? select(row, 'left') : null,
                    children: "◎"
                }),
                /*#__PURE__*/ _jsx("span", {
                    className: rightCss,
                    onClick: ()=>!rightDisabled ? select(row, 'right') : null,
                    children: "◎"
                })
            ]
        });
    };
    const getWikiState = (row)=>{
        if (row?.state) return i18n.get(`wiki_state_${row.state}`);
        return null;
    };
    const getMimeTypeLabel = (row)=>{
        let mt = row.mimetype || revisions.find((r)=>!isNil(r.text) && r.id < row.id)?.mimetype || '';
        if (mt.includes('hocr')) {
            return 'hOCR';
        } else if (mt.includes('markdown')) {
            return 'Markdown';
        } else if (mt.includes('html')) {
            return 'HTML';
        } else if (mt.includes('xml')) {
            return 'XML';
        }
    };
    let i18n = useSelector(Sel.i18n);
    let columns = [
        {
            id: 'choose',
            maxWidth: 50,
            accessor: (row)=>choose(row),
            on: !noDiffMode
        },
        {
            Header: i18n.get('sort_date'),
            id: 'date',
            accessor: (row)=>formatDateTime(row.date)
        },
        {
            Header: 'User',
            accessor: 'username'
        },
        {
            Header: i18n.get('state'),
            id: 'state',
            accessor: (row)=>getWikiState(row),
            on: accessor == 'owhid'
        },
        {
            Header: 'Format',
            id: 'format',
            accessor: (row)=>getMimeTypeLabel(row),
            on: accessor == 'owhid'
        }
    ].filter((c)=>c.on || isNil(c.on));
    return /*#__PURE__*/ _jsx(TranslatedTable, {
        className: `-striped -highlight ${styles.historyTable}`,
        data: revisions,
        columns: columns,
        defaultPageSize: revisions.length,
        getTrProps: (state, row)=>{
            if (!noDiffMode) return {};
            return {
                className: clsx(styles.selectableRow, row.original.id.toString() === selected.right ? styles.selected : null),
                onClick: ()=>setSelected({
                        ...selected,
                        right: row.original.id,
                        left: null
                    })
            };
        }
    }, revisions.length);
};
